/**
 * /guitar/fret-scale-degrees-caged — Scale Degrees in Shapes (CAGED-linked).
 *
 * A key-establishing cadence plays; the tonic-chord CAGED shape lights on the
 * neck (do/mi/sol). Then a single scale degree plays; the user taps where that
 * degree sits inside the box — the non-chord tones (re/fa/la/ti) that fill in
 * the scale around the chord. Single tap auto-submits.
 *
 * The melodic bridge from chord shapes to their parent scale. Filters: shape
 * (E/A/G/C/D / Mixed) + session length.
 */

import { useState, useMemo, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  expandNode,
  MVP_NODES,
  EXERCISE_SKILL_NODES,
  type Item,
} from "@music-app/learning-system";
import { usePrefsStore } from "@music-app/persistence";
import { ModeSwitcherBar, MODE_COLOR, type ExerciseMode } from "../components/ModeSwitcherBar.js";
import { GuitarDrillCore, GuitarNodeStats } from "./FretboardExercise.js";
import { PageHeader } from "../components/PageHeader.js";
import { buttonClasses } from "../components/Button.js";

// ─── Item pool ────────────────────────────────────────────────────────────────

const NODE_IDS = EXERCISE_SKILL_NODES["fret-scale-degrees-caged"];
const ALL_ITEMS: Item[] = NODE_IDS.flatMap((id) =>
  expandNode(MVP_NODES.find((n) => n.id === id)!),
);

const STAT_ROWS = [{ nodeId: "fret-scale-degrees-caged", label: "" }] as const;

type ShapeFilter = "E" | "A" | "G" | "C" | "D" | "mixed";

const SHAPES: ShapeFilter[] = ["E", "A", "G", "C", "D", "mixed"];
const SHAPE_LABELS: Record<ShapeFilter, string> = {
  E: "E", A: "A", G: "G", C: "C", D: "D", mixed: "Mixed",
};

const SESSION_LENGTHS = [5, 10, 15, 20, 30];
const DEFAULT_SESSION_LENGTH = 10;

// ─── Page ─────────────────────────────────────────────────────────────────────

export function FretScaleDegreesCagedExercisePage(): ReactNode {
  const [searchParams] = useSearchParams();
  const rawMode = searchParams.get("mode") as ExerciseMode | null;
  const mode: Exclude<ExerciseMode, "challenge"> =
    rawMode === "listen" || rawMode === "review" ? rawMode : "practice";
  const from = searchParams.get("from") ?? undefined;
  const coached = searchParams.get("coached") === "true";

  const [sessionLength, setSessionLength] = useState(DEFAULT_SESSION_LENGTH);
  const [shapeFilter, setShapeFilter] = useState<ShapeFilter>("mixed");

  const nodeItems = useMemo(() => {
    if (shapeFilter === "mixed") return ALL_ITEMS;
    const items = ALL_ITEMS.filter((i) => i.params["shape"] === shapeFilter);
    return items.length > 0 ? items : ALL_ITEMS;
  }, [shapeFilter]);

  return (
    <GuitarDrillCore
      skillId="fret-scale-degrees-caged"
      nodeItems={nodeItems}
      exercisePath="/guitar/fret-scale-degrees-caged"
      drillTitle="Scale Degrees in Shapes"
      mode={mode}
      from={from}
      coached={coached}
      sessionLength={sessionLength}
      renderSetupScreen={(onStart, ready) => (
        <SetupScreen
          mode={mode}
          from={from}
          shapeFilter={shapeFilter}
          onShapeFilter={setShapeFilter}
          sessionLength={sessionLength}
          onSessionLength={setSessionLength}
          ready={ready}
          onStart={onStart}
        />
      )}
    />
  );
}

// ─── Setup screen ─────────────────────────────────────────────────────────────

function SetupScreen({
  mode,
  from,
  shapeFilter,
  onShapeFilter,
  sessionLength,
  onSessionLength,
  ready,
  onStart,
}: {
  mode: Exclude<ExerciseMode, "challenge">;
  from?: string;
  shapeFilter: ShapeFilter;
  onShapeFilter: (s: ShapeFilter) => void;
  sessionLength: number;
  onSessionLength: (n: number) => void;
  ready: boolean;
  onStart: () => void;
}): ReactNode {
  const isLessonCompleted = usePrefsStore((s) => s.isLessonCompleted);
  const lessonDone = isLessonCompleted("fret-scale-degrees-caged");
  const modeColor = MODE_COLOR[mode];

  const ACTIVE   = { borderColor: modeColor, backgroundColor: modeColor, color: "#FAF6EE" };
  const INACTIVE = { borderColor: "rgba(26,24,19,0.2)", backgroundColor: "#FAF6EE", color: "#1A1813" };
  const [showCustomLength, setShowCustomLength] = useState(false);

  const backLink =
    from === "today" ? { label: "← Today", href: "/" } :
    from === "learn" ? { label: "← Learn", href: "/learn" } :
    { label: "← Guitar Hub", href: "/guitar" };

  return (
    <div className="max-w-2xl mx-auto py-9 px-8 pb-16">
      <PageHeader
        back={backLink}
        title="Scale Degrees in Shapes"
        subhead="The tonic shape is lit — tap the scale degree that fills in the box."
      />

      <ModeSwitcherBar current={mode} exercisePath="/guitar/fret-scale-degrees-caged" />

      <GuitarNodeStats rows={STAT_ROWS} />

      {/* Lesson prompt (introductory — not a hard gate) */}
      {!lessonDone && (
        <div className="mb-6 p-4 border border-accent/30 bg-paperWarm">
          <p className="font-serif italic text-sm text-ink mb-3">
            New here? The lesson shows how the tonic chord shape holds three scale
            degrees — and where the other four hide inside the box.
          </p>
          <Link
            to="/learn/fret-scale-degrees-caged"
            className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent hover:text-ink transition-colors"
          >
            Read lesson →
          </Link>
        </div>
      )}

      {/* Shape selector */}
      <section className="mb-6">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-3">
          Shape
        </p>
        <div className="flex flex-wrap gap-2">
          {SHAPES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => onShapeFilter(s)}
              aria-pressed={shapeFilter === s}
              className="px-3 py-1.5 font-mono text-xs border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
              style={shapeFilter === s ? ACTIVE : INACTIVE}
            >
              {SHAPE_LABELS[s]}
            </button>
          ))}
        </div>
      </section>

      <section className="mb-8">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-3">
          Exercises per session
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          {SESSION_LENGTHS.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => { onSessionLength(n); setShowCustomLength(false); }}
              aria-pressed={!showCustomLength && sessionLength === n}
              className="px-4 py-1.5 font-mono text-xs border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
              style={
                !showCustomLength && sessionLength === n
                  ? { borderColor: modeColor, backgroundColor: modeColor, color: "#FAF6EE" }
                  : { borderColor: "rgba(26,24,19,0.2)", backgroundColor: "#FAF6EE", color: "#1A1813" }
              }
            >
              {n}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setShowCustomLength(true)}
            aria-pressed={showCustomLength}
            className="px-4 py-1.5 font-mono text-xs border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
            style={showCustomLength ? { borderColor: modeColor, backgroundColor: modeColor, color: "#FAF6EE" } : { borderColor: "rgba(26,24,19,0.2)", backgroundColor: "#FAF6EE", color: "#1A1813" }}
          >
            Other…
          </button>
          {showCustomLength && (
            <input
              type="number"
              min={1}
              max={50}
              value={sessionLength}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                if (!isNaN(v) && v >= 1 && v <= 50) onSessionLength(v);
              }}
              className="w-16 px-3 py-1.5 font-mono text-xs border border-ink/20 bg-paper text-ink text-center focus:outline-none focus:border-ink"
              aria-label="Custom number of exercises"
              autoFocus
            />
          )}
        </div>
      </section>

      <button
        type="button"
        onClick={onStart}
        disabled={!ready}
        aria-busy={!ready}
        className={`mt-8 ${buttonClasses("primary")}`}
        style={{ backgroundColor: MODE_COLOR[mode] }}
      >
        {ready ? "Start →" : "Loading sounds…"}
      </button>
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mt-4">
        {ready ? "Audio starts on tap." : "Preparing the instrument…"}
      </p>
    </div>
  );
}
